import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { CollapsedPopover } from "@/components/doc/right-sidebar/CollapsedPopover";
import { Icon } from "@/components/icons/Icon";
import { Input } from "@/components/input/Input";
import { InputSize } from "@/components/input/InputSize";
import { Sidebar } from "@/components/sidebar";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";

import { AlignmentButtons } from "../alignment-buttons/AlignmentButtons";
import { BlockColorPalette } from "../text-style/BlockColorPalette";
import { BlockFormatStyle } from "../text-style/BlockFormatStyle";

import type { DocumentModel } from "@/models/DocumentModel";

const Content = observer(function Content({ doc }: { doc: DocumentModel }) {
  const tImage = useTranslations("sidebar.imageStyle");
  const tSidebar = useTranslations("sidebar");
  const { tiptap, editorState } = doc.editor;
  const [showLink, setShowLink] = useState(false);
  const [link, setLink] = useState("");
  const { imageWidth, imageHeight } = editorState;

  if (!tiptap) {
    return null;
  }

  const handleWidthChange = (newWidth: number) => {
    if (imageWidth && imageHeight) {
      const newHeight = newWidth * (imageHeight / imageWidth);
      tiptap.chain().setWidth(newWidth).setHeight(newHeight).run();
    }
  };

  return (
    <>
      <BlockFormatStyle doc={doc} wrapOnly />
      <Sidebar.Delimiter className="my-1.25" />
      <div className="flex min-h-9.5 items-center">
        <InputSize
          value={imageWidth ?? ""}
          type="width"
          onChange={handleWidthChange}
          label={tImage("imageWidth")}
        />
      </div>
      <Sidebar.Delimiter className="my-1.25" />
      <SidebarToolbar label={tSidebar("toolbars.image")} className="flex-1 justify-between">
        <AlignmentButtons doc={doc} />
        {editorState.blockBackgroundColor && <BlockColorPalette doc={doc} />}
      </SidebarToolbar>
      {showLink && (
        <div className="flex flex-1 items-center justify-between mt-1.25">
          <Input
            value={link}
            autoFocus
            placeholder="https://"
            onChange={(e) => setLink(e.target.value)}
            onCrossClick={() => {
              setLink("");
              setShowLink(false);
            }}
          />
        </div>
      )}
    </>
  );
});

type Props = {
  doc: DocumentModel;
  isCollapsed?: boolean;
};

export const ImageStyle = observer(function ImageStyle({ doc, isCollapsed }: Props) {
  const t = useTranslations("sidebar.blocks");
  const { isImageLoaded } = doc.editor.editorState;

  if (!isImageLoaded) {
    return null;
  }

  if (isCollapsed) {
    return (
      <div className="flex flex-col gap-1 rounded-8 p-0.75 bg-gray-100 dark:bg-gray-900">
        <CollapsedPopover
          icon={<Icon name="Image2" />}
          tooltipTitle={t("image.title")}
          title={t("image.title")}
        >
          <Content doc={doc} />
        </CollapsedPopover>
      </div>
    );
  }

  return (
    <ActionsDisclosure
      data-panel="image_style"
      title={t("image.title")}
      disclosureProps={{ defaultOpen: true }}
      disclosurePanelProps={{ className: "-mt-1.25" }}
    >
      <Content doc={doc} />
    </ActionsDisclosure>
  );
});
